import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'
import {
  useAttractionsStore,
  useAuthStore,
  useEventsStore,
  useFacilitiesStore,
  useGalleriesStore,
  useHotelStore,
  useLabelsStore,
  useMeetingsStore,
  useNewsStore,
  useOffersStore,
  usePagesStore,
  useRoomsStore,
  useSettingStore,
} from 'pinia-store'
import type { IPress } from '@ui/interfaces/press'
import type { IOffers } from '@ui/interfaces/offers'
import type { IRooms } from '@ui/interfaces/rooms'
import { PAGE_PARAMS } from 'pinia-store/pages'
import { useHeadMeta } from '@ui/composables/useHeadMeta'
import { useCookie } from '#imports'

// Exports
export function useDataHome() {
  const stores = {
    auth: useAuthStore(),
    setting: useSettingStore(),
    rooms: useRoomsStore(),
    news: useNewsStore(),
    offers: useOffersStore(),
    facilities: useFacilitiesStore(),
    galleries: useGalleriesStore(),
    hotel: useHotelStore(),
    labels: useLabelsStore(),
    attractions: useAttractionsStore(),
    meetings: useMeetingsStore(),
    events: useEventsStore(),
    pages: usePagesStore(),
  }
  const bool = useCookie(`has_splash_${stores.setting.version}`)
  const usePages = usePagesStore()
  const profile = computed(() => stores.hotel.getProfile)
  const announcement = computed(() => stores.hotel.getAnnouncement)
  const labels = computed(() => stores.labels.getLabels)
  const attractions = computed(() => stores.attractions.getList)
  const loadingOffers = computed(() => stores.offers.loading)
  const galleries = computed(() => stores.galleries.list)
  const facilities = computed(() => stores.facilities.getList)
  const meetings = computed(() => stores.meetings.getList)
  const events = computed(() => stores.events.getList)
  const menu = computed(() => stores.pages.getMenu)
  const content = usePages.getProfile('home')

  const disableSplashScreen = computed(() => stores.setting?.disableSplashScreen)
  const aboutImages = computed(() => galleries.value.filter((gallery: any) => gallery.is_thumbnail_about))

  const offers = ref<IOffers>([])
  const press = ref<IPress>([])
  const rooms = ref<IRooms>([])
  const isModalSplash = ref(false)

  const token = stores.auth.getDataToken

  function updateDataFromStores() {
    offers.value = stores.offers.home?.offers || []
    press.value = stores.news.home?.press || []
    rooms.value = stores.rooms.data?.rooms || []
  }

  onMounted(updateDataFromStores)
  watchEffect(() => {
    useHeadMeta(content)
  })
  watch(() => stores.news.home, (cur) => {
    press.value = cur?.press || []
  }, { deep: true, immediate: true })

  watch(() => stores.rooms.data, (cur) => {
    rooms.value = cur?.rooms || []
  }, { deep: true, immediate: true })

  watch(() => stores.offers.home, (cur) => {
    offers.value = cur?.offers || []
  }, { deep: true, immediate: true })

  async function fetchData() {
    if (!token)
      return

    const currentToken = { headers: { Authorization: `Bearer ${token}` } }
    const paramsOffers = { ...currentToken }
    const paramsNews = { ...currentToken }

    const arrOffers = menu.value?.filter(el => el.slug === 'offers')
    const arrNews = menu.value?.filter(el => el.slug === 'whats-new')
    const arrRooms = menu.value?.filter(el => el.slug === 'rooms')

    if (arrOffers?.length && !arrOffers[0]?.is_spa)
      Object.assign(paramsOffers, PAGE_PARAMS)

    if (arrNews?.length)
      Object.assign(paramsNews, arrNews[0]?.is_spa ? { page: 1, page_size: 8 } : PAGE_PARAMS)

    if (arrOffers?.length)
      await stores.offers.setData(paramsOffers, 'home')
    if (arrNews?.length)
      await stores.news.setData(paramsNews, 'home')
    if (arrRooms?.length)
      await stores.rooms.setRate(currentToken)
  }
  const splashOffers = computed(() => {
    let arr: any = []
    if (announcement.value && Array.isArray(announcement.value) && announcement.value.length > 0) {
      arr = [...announcement.value.filter(el => el.status === 'ACTIVE').map(({
        announcement_id: id,
        title,
        image,
        cta: link,
        start_date,
        end_date,
        status,
      }) => ({
        id,
        title,
        image,
        link,
        start_date,
        end_date,
        status,
      }))]
    }
    if (offers.value && Array.isArray(offers.value) && offers.value.length) {
      const tmp = offers.value.filter((el: any) => el?.is_highlight)
      if (tmp && tmp.length) {
        arr = [...arr, ...tmp.map(({
          offers_title: title,
          offers_link: link,
          category,
          promo_code,
          offers_image: image,

        }) => ({
          title,
          link,
          category,
          promo_code,
          image,
        }))]
      }
    }
    return arr
  })

  watchEffect(() => {
    if (splashOffers.value && splashOffers.value.length
      && (!bool.value && !stores.setting.hasSplashScreen)) {
      isModalSplash.value = true
    }
    else {
      stores.setting.setHasSplashScreen(true)
      isModalSplash.value = false
    }
  })

  onBeforeMount(() => fetchData())
  return {
    labels,
    attractions,
    loadingOffers,
    facilities,
    aboutImages,
    announcement,
    galleries,
    profile,
    meetings,
    events,
    press,
    offers,
    rooms,
    splashOffers,
    isModalSplash,
    disableSplashScreen,
    token,
  }
}
